import { RmaEntityTypeCodes } from '@harvestiq/constants';
import { EntityWithLatestHistory } from './search-entities-request';
import { makeFullName } from '@harvestiq/utils';
import { EntityHistory } from '../entities-history/entity-history';
import { isEntityFirstNameLastNameAllowedAndAvailable } from './is-entity-firstName-lastName-allowed';

type SignatureFormatter = (
  data: EntityWithLatestHistory | EntityHistory
) => string;

const commonSignatureFormatter = (
  data: EntityWithLatestHistory | EntityHistory,
  suffix = ''
) => {
  const hasOwnerName = data.owner?.firstName || data.owner?.lastName;
  const byOwnerText = hasOwnerName
    ? ` by ${makeFullName(data.owner?.firstName, data.owner?.lastName)}`
    : '';

  return [data.businessName, byOwnerText, suffix].filter(Boolean).join('');
};

const signatureFormatters: Record<RmaEntityTypeCodes, SignatureFormatter> = {
  [RmaEntityTypeCodes.BureauOfIndianAffairsAndIndianTribeTreaties]: (data) =>
    commonSignatureFormatter(data, ', Trustee'),
  [RmaEntityTypeCodes.Corporation]: (data) => commonSignatureFormatter(data),
  [RmaEntityTypeCodes.Estates]: (data) =>
    commonSignatureFormatter(data, ', Executor'),
  [RmaEntityTypeCodes.IndividualOperatingAsABusiness]: (data) =>
    commonSignatureFormatter(data, ', Sole Owner'),
  [RmaEntityTypeCodes.JointOperationsVenturesAndCoOwnerships]: (data) =>
    makeFullName(data.firstName, data.lastName),
  [RmaEntityTypeCodes.LimitedLiabilityCompany]: (data) =>
    commonSignatureFormatter(data),
  [RmaEntityTypeCodes.NonProfitOrTaxExemptOrganizations]: (data) =>
    commonSignatureFormatter(data),
  [RmaEntityTypeCodes.Partnership]: (data) => {
    const hasOwnerName = data.owner?.firstName || data.owner?.lastName;
    const byOwnerText = hasOwnerName
      ? `, by ${makeFullName(data.owner?.firstName, data.owner?.lastName)}`
      : '';
    return [data.businessName, ', A Partnership', byOwnerText, ', A Partner']
      .filter(Boolean)
      .join('');
  },
  [RmaEntityTypeCodes.PublicSchools]: (data) => commonSignatureFormatter(data),
  [RmaEntityTypeCodes.ReceiverOrLiquidator]: (data) =>
    commonSignatureFormatter(data, ', Receiver'),
  [RmaEntityTypeCodes.StateLocalGovernment]: (data) =>
    commonSignatureFormatter(data),
  [RmaEntityTypeCodes.TrustRevocable]: (data) =>
    commonSignatureFormatter(data, ', Trustee'),
  [RmaEntityTypeCodes.TrustIrrevocable]: (data) =>
    commonSignatureFormatter(data, ', Trustee'),
  [RmaEntityTypeCodes.TransferOfRightToIndemnity]: (data) =>
    commonSignatureFormatter(data),

  // It should not make to this point if name is not allowed
  [RmaEntityTypeCodes.Individuals]: function (): string {
    console.error('Function not implemented.');
    return '';
  },
  [RmaEntityTypeCodes.SpousalMarried]: function (): string {
    console.error('Function not implemented.');
    return '';
  },
};

export const generateEntitySignature = (
  entity: EntityWithLatestHistory | EntityHistory
) => {
  const isEntityNameAllowedAndAvailable =
    isEntityFirstNameLastNameAllowedAndAvailable({
      taxIdType: entity.taxIdType,
      entityTypeCode: entity.type,
      firstName: entity.firstName,
      lastName: entity.lastName,
    });

  if (isEntityNameAllowedAndAvailable) {
    return makeFullName(entity.firstName, entity.lastName);
  }

  if (!isEntityNameAllowedAndAvailable && !entity.businessName) {
    console.error('Entity does not have a name to generate signature.');
    return '';
  }

  const formatter = signatureFormatters[entity.type];

  try {
    // Use the appropriate signature formatter for the entity type
    return formatter(entity);
  } catch (error) {
    console.error('Error formatting customer signature:', error);
    return '';
  }
};
